<template>
    <USlideover :model-value="open" @update:model-value="$emit('update:open', $event)" prevent-close>
        <UForm ref="form" @submit="onSubmit" :schema="schema" :state="state" :validate-on="['submit']" class="h-full">
            <UCard>
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-base font-medium leading-6 text-gray-800">Create Multiple Coupons</h3>
                    </div>
                </template>
                <div class="space-y-6">
                    <UFormGroup name="partner_id" label="Partner" required>
                        <UInputMenu
                            v-model="partner.selected"
                            @update:model-value="state.partner_id = $event.id"
                            :loading="partner.searching"
                            :search="searchPartner"
                            placeholder="Search for a partner..."
                            option-attribute="name"
                            size="xl"
                            trailing
                            by="id"
                        />
                    </UFormGroup>
                    <UFormGroup name="count" label="Number of Coupons" required>
                        <UInput v-model="state.count" type="number" size="xl" min="1" max="1000" />
                    </UFormGroup>
                    <div class="grid grid-cols-2 gap-4">
                        <UFormGroup name="validity" label="Validity" required>
                            <UInput v-model="state.validity" type="number" size="xl">
                                <template #trailing>
                                    {{ state.validity === 1 ? 'day' : 'days' }}
                                </template>
                            </UInput>
                        </UFormGroup>
                        <UFormGroup name="grace_period_days" label="Grace Period" required>
                            <UInput v-model="state.grace_period_days" type="number" size="xl">
                                <template #trailing>
                                    {{ state.grace_period_days === 1 ? 'day' : 'days' }}
                                </template>
                            </UInput>
                        </UFormGroup>
                    </div>
                    <UFormGroup name="max_uses" label="Maximum Uses" required>
                        <UInput v-model="state.max_uses" type="number" size="xl" leading />
                    </UFormGroup>
                    <UFormGroup name="expires_at" label="Expiry Date" hint="Optional">
                        <UInput v-model="state.expires_at" type="date" size="xl" />
                    </UFormGroup>
                </div>
                <template #footer>
                    <div class="flex items-center justify-end space-x-2">
                        <template v-if="!insertedIds.length">
                            <UButton @click="dismiss()" size="lg" color="white"> Cancel </UButton>
                            <UButton type="submit" size="lg" :loading="loading" color="black"> Create </UButton>
                        </template>
                        <UButton v-else @click="onDownload()" size="lg" color="primary"> Download &amp; Close </UButton>
                    </div>
                </template>
            </UCard>
        </UForm>
    </USlideover>
</template>

<script setup lang="ts">
import { z } from 'zod'
import type { FormSubmitEvent } from '#ui/types'
import type { Partner } from '~/types/partner'

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [boolean]
}>()

const props = defineProps<{
    open: boolean
}>()

const client = useSupabaseClient()

const form = ref()
const state = ref()
const loading = ref<boolean>(false)
const insertedIds = ref<string[]>([])

const partner = ref<{
    searching: boolean
    selected?: Partial<Partner>
}>({
    searching: false,
})

const schema = z.object({
    partner_id: z.string(),
    count: z.number().min(1).max(1000),
    validity: z.number().min(1),
    grace_period_days: z.number().min(0),
    max_uses: z.number().min(1),
    expires_at: z.string().optional(),
})

watch(
    () => props.open,
    (open) => {
        if (open) {
            partner.value.selected = undefined
            insertedIds.value = []

            state.value = {
                partner_id: undefined,
                count: 1,
                validity: 365,
                grace_period_days: 30,
                max_uses: 1,
                expires_at: undefined,
            }
        }
    },
)

async function searchPartner(q: string) {
    partner.value.searching = true

    const { data, error } = await client.from('partners')
        .select('id, name')
        .is('deleted_at', null)
        .ilike('name', `%${q}%`)

    if (error) {
        return []
    }

    partner.value.searching = false
    return data
}

function generateCode(length: number) {
    const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
    let result = ''
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * characters.length))
    }
    return result
}

async function onSubmit(event: FormSubmitEvent<z.infer<typeof schema>>) {
    loading.value = true

    const coupons = [...Array(event.data.count).keys()].map(() => {
        return {
            partner_id: event.data.partner_id,
            code: generateCode(12),
            validity: event.data.validity,
            grace_period_days: event.data.grace_period_days,
            max_uses: event.data.max_uses,
            expires_at: event.data.expires_at,
        }
    })

    const { data, error } = await client.from('coupons')
        .insert(coupons as never)
        .select('id')

    loading.value = false

    if (error) {
        if (error.code === '23505') {
            form.value.setErrors([{ path: 'code', message: 'Specified code has already been used.' }])
        }
    } else {
        insertedIds.value = data.map((c) => c.id)
    }
}

async function onDownload() {
    if (insertedIds.value.length) {
        const { data } = await client.from('coupons')
            .select('code, validity, grace_period_days, max_uses, expires_at')
            .in('id', insertedIds.value)
            .is('deleted_at', null)
            .csv()

        if (data) {
            useFile().downloadCsv(data, 'coupons.csv')
        }

        dismiss(true)
    }
}

function dismiss(reload: boolean = false) {
    emit('update:open', false)

    if (reload) {
        emit('reload', true)
    }
}
</script>
